import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { TerminalDeLaSesion } from '../core/offline/terminal-de-la-sesion';

/** Gasto de caja registrado durante el turno (F5, cluster caja). */
export interface RegisterExpense {
  id: number;
  expenseDate: string;
  concept: string;
  amount: number;
  createdBy: string | null;
  createdAt: string;
  /** Caja menor registrada: un gasto anulado deja de restar de la caja y conserva quién y por qué. */
  anuladoEn?: string | null;
  anuladoPor?: string | null;
  motivoAnulacion?: string | null;
}

/** Una categoría de gasto (core expense_categories) o un proveedor (core suppliers), para elegir en la caja menor. */
export interface OpcionDeGasto { id: number; nombre: string; }

/** B9.5 (-mt V210): la categoría y el proveedor de la caja menor, opcionales. */
export interface TerceroDelGasto { categoryId?: number | null; supplierId?: number | null; }

/**
 * Gastos de caja en cualquier momento del turno: se registran desde el sidebar
 * y el cierre del día los precarga como gastos menores.
 */
@Injectable({ providedIn: 'root' })
export class ExpenseService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/expenses`;
  private terminal = inject(TerminalDeLaSesion);
  private core = environment.coreApiBaseUrl;

  /*
   * Caja menor POR CAJA (AUDITORIA-EFECTIVO-ESPERADO §2): el gasto lleva la caja que lo paga, y el del día se pide
   * solo de esta caja, así el cierre no descuenta los de las otras. Sin UUID, como hoy: todos los del negocio.
   */

  async getToday(incluirAnulados = false): Promise<RegisterExpense[]> {
    const params = { ...await this.terminal.params(), ...(incluirAnulados ? { incluirAnulados: 'true' } : {}) };
    return firstValueFrom(this.http.get<RegisterExpense[]>(this.base, { params }));
  }

  /**
   * `idempotencyKey`: la del gasto, creada al escribirlo (DrDev002). Si el POST llega y la respuesta se pierde, el reintento
   * lleva la misma y el servidor devuelve la fila que ya existe en vez de restarlo dos veces.
   */
  async create(concept: string, amount: number, createdBy?: string, idempotencyKey?: string, tercero: TerceroDelGasto = {}): Promise<RegisterExpense> {
    // fail-on-unknown en -mt: categoryId y supplierId solo viajan si se eligieron. Un -mt anterior a V210 da 400 con ellos.
    const { categoryId, supplierId } = tercero;
    return firstValueFrom(this.http.post<RegisterExpense>(this.base,
      { concept, amount, createdBy, ...(idempotencyKey ? { idempotencyKey } : {}), ...await this.terminal.params(),
        ...(categoryId != null ? { categoryId } : {}), ...(supplierId != null ? { supplierId } : {}) }));
  }

  /** Categorías activas del negocio (core). Tope de 3 s: el diálogo no espera a una lista que no llega. */
  categorias(): Promise<OpcionDeGasto[]> {
    return firstValueFrom(this.http.get<{ id: number; name: string }[]>(`${this.core}/expense-categories`).pipe(
      timeout(3000), map(l => l.map(c => ({ id: c.id, nombre: c.name })))));
  }

  /** Proveedores activos del negocio (core). */
  proveedores(): Promise<OpcionDeGasto[]> {
    return firstValueFrom(this.http.get<{ id: number; name: string }[]>(`${this.core}/api/suppliers`).pipe(
      timeout(3000), map(l => l.map(p => ({ id: p.id, nombre: p.name })))));
  }

  /** Un gasto registrado no se borra: se anula con motivo (400 sin motivo). */
  anular(id: number, motivo: string): Promise<RegisterExpense> {
    return firstValueFrom(this.http.post<RegisterExpense>(`${this.base}/${id}/anular`, { motivo }));
  }
}
